import { useGSAP } from '@gsap/react';
import { PortableText } from '@portabletext/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import bottomCloud from '../../../images/textAndImages_cloud_bottom.png';
import topCloud from '../../../images/textAndImages_cloud_top.png';
import { getComponents, getPortableTextTimeline } from '../../../portableText';
import { RawPortableText } from '../../../types/types';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import ButtonLink from '../ButtonLink';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './TextAndImagesModule.module.scss';

export const TextAndImagesModuleFragment = graphql`
  fragment TextAndImagesModule on SanityTextAndImagesModule {
    imagesSide
    withLargerFirstImage
    largeImage {
      ...SanityImage
    }
    smallImage {
      ...SanityImage
    }
    images {
      ...SanityImage
    }
    illustration {
      ...SanityImage
    }
    lightIllustration {
      ...SanityImage
    }
    title {
      ...LocaleStringFragment
    }
    Text {
      ...LocaleListPortableText
    }
    primaryButton {
      ...ButtonLink
    }
    secondaryButton {
      ...ButtonLink
    }
  }
`;

export type TextAndImagesModuleProps = {
  imagesSide: 'left' | 'right';
  illustration?: SanityImageType;
  lightIllustration?: SanityImageType;
  title?: string;
  Text: RawPortableText;
  primaryButton?: ButtonLinkType;
  secondaryButton?: ButtonLinkType;
  className?: string;
} & (
  | {
      withLargerFirstImage: true;
      largeImage: SanityImageType;
      smallImage: SanityImageType;
      images?: never;
    }
  | {
      withLargerFirstImage: false;
      largeImage?: never;
      smallImage?: never;
      images?: Array<SanityImageType>;
    }
);

function TextAndImagesModule(
  props: TextAndImagesModuleProps & CommonModuleProps,
): React.ReactElement {
  const {
    title,
    imagesSide,
    withLargerFirstImage,
    largeImage,
    smallImage,
    images,
    illustration,
    lightIllustration,
    Text,
    primaryButton,
    secondaryButton,
    className,
    moduleId,
  } = props;
  const containerRef = useRef<HTMLElement | null>(null);

  // useScrollLinkedClouds(`.${styles.topCloud}, .${styles.bottomCloud}`, {
  //   scope: containerRef,
  // });

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 75%',
        } satisfies ScrollTrigger.Vars,
      });

      tl.addLabel('start');

      // portable text
      tl.add(getPortableTextTimeline(styles.text));

      // buttons
      gsap.config({ nullTargetWarn: false });
      tl.from(
        `.${styles.buttonsContainer}`,
        {
          y: 30,
          autoAlpha: 0,
          duration: 0.5,
        },
        '>-2',
      );
      gsap.config({ nullTargetWarn: true });

      // IMAGES
      const imagesTl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 75%',
        } satisfies ScrollTrigger.Vars,
      });

      imagesTl.from(`.${styles.mediaContainer} > div`, {
        y: 100,
        autoAlpha: 0,
        stagger: 0.8,
        duration: 1.5,
      });
      imagesTl.addLabel('entryFinished');
      gsap.utils
        .toArray<HTMLDivElement>(
          `.${styles.mediaContainer} > div, .${styles.topCloud}, .${styles.bottomCloud}`,
        )
        .forEach((imageContainer, i) => {
          imagesTl.to(
            imageContainer,
            {
              y: 20,
              repeat: -1,
              delay: i * 0.5,
              duration: 3,
              yoyo: true,
              ease: 'power1.inOut',
            },
            'entryFinished',
          );
        });
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      ref={containerRef}
      id={moduleId}
      className={clsx(className)}
      currentModuleBgColor={props.theme}
      contentClassName={clsx(styles.container, withLargerFirstImage && styles.withLargerFirstImage)}
    >
      {title && (
        <AnimatedTitle
          title={title}
          trigger={styles.contentContainer}
          resolution="mobile"
          className={clsx(styles.title, styles.mobile)}
        />
      )}
      <div
        className={clsx(styles.contentContainer, imagesSide === 'right' && styles.mediaOnTheRight)}
      >
        {withLargerFirstImage ? (
          <div className={styles.mediaContainer}>
            <div className={styles.largeImageContainer}>
              <Image
                image={largeImage}
                className={styles.largeImage}
                width={1000}
                height={1400}
                cover
              />
            </div>
            <div className={styles.smallImageContainer}>
              <Image
                image={smallImage}
                className={styles.smallImage}
                width={800}
                height={800}
                cover
              />
            </div>
            {illustration && (
              <div className={styles.illustrationContainer}>
                <Image
                  image={
                    (props.theme === 'starlit' || props.theme === 'moonlit') && lightIllustration
                      ? lightIllustration
                      : illustration
                  }
                  className={styles.illustration}
                />
              </div>
            )}
          </div>
        ) : (
          <div className={styles.mediaContainer}>
            <img src={topCloud} alt="" className={styles.topCloud} />
            {images && images.length > 3 && (
              <img src={bottomCloud} alt="" className={styles.bottomCloud} />
            )}
            {images?.map((image, i) => (
              <div className={clsx(styles.imageContainer, styles['image' + i])} key={i}>
                <Image image={image} />
              </div>
            ))}
          </div>
        )}
        <div className={styles.textContainer}>
          {title && (
            <AnimatedTitle
              title={title}
              trigger={styles.contentContainer}
              resolution="desktop"
              className={clsx(styles.title, styles.desktop)}
            />
          )}
          <div className={styles.text}>
            <PortableText value={Text} components={getComponents(true)} />
          </div>
          {(primaryButton || secondaryButton) && (
            <div className={styles.buttonsContainer}>
              {primaryButton && <ButtonLink button={primaryButton}></ButtonLink>}
              {secondaryButton && <ButtonLink secondary button={secondaryButton}></ButtonLink>}
            </div>
          )}
          {!withLargerFirstImage && illustration && (
            <div className={clsx(styles.mediaContainer, styles.illustrationContainer)}>
              <Image
                image={
                  (props.theme === 'starlit' || props.theme === 'moonlit') && lightIllustration
                    ? lightIllustration
                    : illustration
                }
                className={styles.illustration}
              />
            </div>
          )}
        </div>
      </div>
    </ModuleLayout>
  );
}

export default TextAndImagesModule;
